import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  flip, FloatingFocusManager, FloatingPortal, offset, shift,
  useDismiss, useFloating, useInteractions, useRole
} from '@floating-ui/react'
import Badge from '../../ui/Badge'
import Button from '../../ui/Button'
import Checkbox from '../../ui/Checkbox'
import StatusDot from '../IssueStatuses/StatusDot'
import { CATEGORIES, categoryBadgeVariant } from '../IssueStatuses/constants'

const TRIGGER_BASE = 'tw:flex tw:items-center tw:justify-between tw:gap-2 tw:w-full '
  + 'tw:px-3 tw:py-2 tw:bg-white tw:border tw:border-solid '
  + 'tw:rounded! tw:cursor-pointer tw:text-sm tw:text-[#333333]'
const TRIGGER_BORDER = 'tw:border-[#cccccc] tw:hover:border-[#b3b3b3]'
const TRIGGER_BORDER_OPEN = 'tw:border-ds-primary'

const CHEVRON_PATH = 'M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 '
  + '1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 '
  + '0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s'
  + '-0.436-1.17 0-1.615z'

const PANEL_CLASS = 'tw:z-[1100] tw:w-96 tw:bg-white tw:border tw:border-solid '
  + 'tw:border-gray-300 tw:rounded-lg tw:shadow-lg tw:flex tw:flex-col tw:overflow-hidden'
const HEADER_CLASS = 'tw:flex tw:flex-col tw:gap-2 tw:p-3'
const HEADER_ROW_CLASS = 'tw:flex tw:items-center tw:justify-between'
const CLEAR_CLASS = 'tw:bg-transparent tw:border-0 tw:p-0 tw:text-xs! tw:font-medium '
  + 'tw:text-gray-500 tw:cursor-pointer tw:hover:text-gray-700'
// Maquette 482-14144: filled grey field, magnifier on the left (the DS SearchInput
// Is white/bordered with the magnifier on the right, so it is not reused here).
const SEARCH_ICON_CLASS = 'fas fa-search tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 '
  + 'tw:text-gray-500 tw:text-sm tw:pointer-events-none'
const SEARCH_INPUT_CLASS = 'tw:w-full tw:h-9 tw:pl-9 tw:pr-3 tw:bg-gray-100! tw:border-0! '
  + 'tw:rounded-lg! tw:text-sm tw:text-gray-900 tw:placeholder:text-gray-500 '
  + 'tw:outline-none! tw:shadow-none! tw:focus-visible:ring-2 tw:focus-visible:ring-ds-primary'
const BODY_CLASS = 'tw:flex tw:flex-col tw:gap-3 tw:px-3 tw:py-1 tw:max-h-80 tw:overflow-y-auto'
const CAT_HEADER_CLASS = 'tw:flex tw:items-center tw:gap-2 tw:pl-1.5 tw:pr-2 tw:py-1.5'
const CAT_TOGGLE_CLASS = 'tw:flex-1 tw:flex tw:items-center tw:justify-between tw:gap-2 '
  + 'tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer'
const COUNTER_CLASS = 'tw:text-xs tw:font-medium tw:text-gray-500'
// `tw:flex!`/`tw:mb-0!` beat Bootstrap reboot's out-of-layer label rules (inline-block + margin).
const ROW_CLASS = 'tw:flex! tw:items-center tw:gap-2 tw:px-2 tw:py-1.5 tw:mb-0! tw:cursor-pointer'
const ROW_NAME_CLASS = 'tw:flex-1 tw:min-w-0 tw:text-sm tw:text-gray-900 tw:truncate'
const FOOTER_CLASS = 'tw:flex tw:items-center tw:justify-between tw:p-3 '
  + 'tw:border-t tw:border-solid tw:border-gray-200'
const COUNT_CLASS = 'tw:text-xs tw:text-gray-500'
const DIVIDER_CLASS = 'tw:border-t tw:border-solid tw:border-gray-200'

const normalize = value => value.toLocaleLowerCase('fr-FR')
const pluralizeSelected = count => `${count} sélectionné${count > 1 ? 's' : ''}`
const triggerLabel = (selectedCount, total) => {
  const count = selectedCount === 0 ? total : selectedCount
  return `Statuts (${count}/${total})`
}

// No applied filter (empty value) means every status is shown, so the panel opens
// With all steps checked rather than empty.
const seedFromValue = (value, statuses) =>
  value.length > 0 ? new Set(value) : new Set(statuses.map(status => String(status.id)))

const StatusFilter = ({ statuses, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState(() => seedFromValue(value, statuses))
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState(() => new Set())

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift({ padding: 8 })]
  })
  const dismiss = useDismiss(context, { outsidePress: true, escapeKey: true })
  const role = useRole(context, { role: 'dialog' })
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role])

  // Re-seed the draft from the applied value each time the panel opens so an
  // Abandoned edit (closed without Appliquer) does not leak into the next open.
  useEffect(() => {
    if (isOpen) {
      setDraft(seedFromValue(value, statuses))
      setSearch('')
    }
  }, [isOpen, value, statuses])

  const groups = useMemo(() => {
    const needle = normalize(search.trim())
    return CATEGORIES.map(category => {
      const items = statuses.filter(status => status.category === category.value)
      const matched = needle ? items.filter(status => normalize(status.name).includes(needle)) : items
      return { ...category, items: matched }
    }).filter(group => group.items.length > 0)
  }, [statuses, search])

  const handleToggleOpen = useCallback(() => setIsOpen(previous => !previous), [])

  const handleToggleToken = useCallback(event => {
    const { token } = event.currentTarget.dataset
    setDraft(previous => {
      const next = new Set(previous)
      if (next.has(token))
        next.delete(token)
      else
        next.add(token)
      return next
    })
  }, [])

  // Tokens come from the rendered (search-filtered) items so "select all" never
  // Touches steps hidden by the current search.
  const handleToggleCategory = useCallback(event => {
    const tokens = event.currentTarget.dataset.tokens.split(',').filter(Boolean)
    setDraft(previous => {
      const next = new Set(previous)
      const allSelected = tokens.every(token => next.has(token))
      tokens.forEach(token => {
        if (allSelected)
          next.delete(token)
        else
          next.add(token)
      })
      return next
    })
  }, [])

  const handleToggleCollapse = useCallback(event => {
    const { category } = event.currentTarget.dataset
    setCollapsed(previous => {
      const next = new Set(previous)
      if (next.has(category))
        next.delete(category)
      else
        next.add(category)
      return next
    })
  }, [])

  const handleClear = useCallback(() => setDraft(new Set()), [])
  const handleApply = useCallback(() => {
    const allTokens = statuses.map(status => String(status.id))
    const allSelected = allTokens.length === draft.size && allTokens.every(token => draft.has(token))
    onChange(allSelected ? [] : [...draft])
    setIsOpen(false)
  }, [draft, onChange, statuses])
  const handleSearchChange = useCallback(event => setSearch(event.target.value), [])

  const isSearching = search.trim().length > 0
  const triggerBorder = isOpen ? TRIGGER_BORDER_OPEN : TRIGGER_BORDER

  const renderGroup = group => {
    const tokens = group.items.map(status => String(status.id))
    const selectedCount = tokens.filter(token => draft.has(token)).length
    const allSelected = selectedCount === tokens.length
    const isCollapsed = !isSearching && collapsed.has(group.value)
    return (
      <div key={group.value} role="group">
        <div className={CAT_HEADER_CLASS}>
          <Checkbox
            aria-label={`Tout sélectionner : ${group.label}`}
            checked={allSelected}
            data-tokens={tokens.join(',')}
            indeterminate={selectedCount > 0 && !allSelected}
            onChange={handleToggleCategory}
          />
          <button
            aria-expanded={!isCollapsed}
            className={CAT_TOGGLE_CLASS}
            data-category={group.value}
            type="button"
            onClick={handleToggleCollapse}
          >
            <Badge variant={categoryBadgeVariant(group.value)}>{group.label}</Badge>
            <span className="tw:flex tw:items-center tw:gap-2">
              <span className={COUNTER_CLASS}>{`${selectedCount}/${tokens.length}`}</span>
              <i
                aria-hidden="true"
                className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'} tw:text-xs tw:text-gray-400`}
              />
            </span>
          </button>
        </div>
        {isCollapsed ? null : (
          <div className="tw:pl-3">
            {group.items.map(status => (
              <label key={status.id} className={ROW_CLASS}>
                <Checkbox
                  checked={draft.has(String(status.id))}
                  data-token={String(status.id)}
                  onChange={handleToggleToken}
                />
                <StatusDot color={status.color} />
                <span className={ROW_NAME_CLASS}>{status.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <button
        ref={refs.setReference}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={`${TRIGGER_BASE} ${triggerBorder}`}
        type="button"
        {...getReferenceProps({ onClick: handleToggleOpen })}
      >
        <span className="tw:truncate">{triggerLabel(value.length, statuses.length)}</span>
        <svg
          aria-hidden="true"
          className="tw:shrink-0 tw:text-[#cccccc]"
          focusable="false"
          height="20"
          viewBox="0 0 20 20"
          width="20"
        >
          <path d={CHEVRON_PATH} fill="currentColor" />
        </svg>
      </button>
      {isOpen ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} initialFocus={-1} modal={false}>
            <div
              ref={refs.setFloating}
              aria-label="Filtrer par statut"
              className={PANEL_CLASS}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <div className={HEADER_CLASS}>
                <div className={HEADER_ROW_CLASS}>
                  <span className="tw:text-sm tw:font-medium tw:text-gray-900">Filtrer</span>
                  <button className={CLEAR_CLASS} type="button" onClick={handleClear}>
                    Tout effacer
                  </button>
                </div>
                <div className="tw:relative tw:w-full">
                  <i aria-hidden="true" className={SEARCH_ICON_CLASS} />
                  <input
                    aria-label="Rechercher un statut"
                    className={SEARCH_INPUT_CLASS}
                    name="status-filter-search"
                    placeholder="Rechercher un statut..."
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>
              <div className={DIVIDER_CLASS} />
              <div className={BODY_CLASS}>
                {groups.map(renderGroup)}
              </div>
              <div className={FOOTER_CLASS}>
                <span className={COUNT_CLASS}>{pluralizeSelected(draft.size)}</span>
                <Button
                  className="tw:bg-ds-neutral-highest! tw:border-ds-neutral-highest!"
                  variant="primary"
                  onClick={handleApply}
                >
                  Appliquer
                </Button>
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  )
}

StatusFilter.propTypes = {
  onChange: PropTypes.func.isRequired,
  statuses: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string,
    color: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired
  })),
  value: PropTypes.arrayOf(PropTypes.string).isRequired
}

StatusFilter.defaultProps = {
  statuses: []
}

export default StatusFilter
